package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.KeyRow
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun EditorAssistContent(
    state: TranslationEditorState,
    row: KeyRow,
    onSuggestionInserted: (EditorSuggestionInsertion) -> Unit,
    announcementsEnabled: Boolean = true,
    announcementSessionKey: Long = 0L,
) {
    val enabled = state.enabledProviders
    Column(
        EditorAssistContentStyle.toModifier()
            .attr("data-editor-assist-content", "true"),
    ) {
        // **The stream is first, and the provider bar rides under its heading.**
        //
        // The panel used to open with a select and a Generate button — a control asking the reader
        // to spend a provider call before showing them the three suggestions that already existed,
        // one of them a 94% translation-memory match. What Assist has is the answer; how to ask for
        // another one is the follow-up, so it sits inside the Suggestions section as the "generate
        // another" affordance rather than above it as the way in.
        RankedAssistStream(
            state = state,
            row = row,
            onSuggestionInserted = onSuggestionInserted,
            announcementsEnabled = announcementsEnabled,
            announcementSessionKey = announcementSessionKey,
        ) {
            if (state.capability.canWriteTranslations) {
                // **`canSpendCredits`, not `canWriteTranslations`, gates every MT affordance.**
                // `POST /mt/translate` requires the `mt:spend` scope *and* role >= ADMIN, so a
                // TRANSLATOR was offered "Generate suggestion" and answered 403 on every press.
                // The control is absent rather than disabled, and the section that replaces it
                // names who may spend — refusing honestly is the rule, and silence is not a
                // refusal.
                if (!state.capability.canSpendCredits) {
                    MachineSpendUnavailableSection()
                } else if (enabled.isNotEmpty()) {
                    MachineProviderSection(state, row, enabled)
                } else if (state.providersLoading && !state.providersUnavailable) {
                    MachineProviderLoadingSection()
                } else if (state.providersUnavailable) {
                    MachineProviderLoadUnavailableSection(
                        loading = state.providersLoading,
                        onRetry = state::retryProviders,
                    )
                } else {
                    MachineProviderUnavailableSection(
                        projectSlug = state.projectSlug,
                        canManageProviders = state.canEditStructure,
                    )
                }
            }
        }
        RelevantGlossary(state, row)
    }
}

@Composable
internal fun AssistSectionHeading(value: String, id: String) {
    H3(EditorAssistSectionLabelStyle.toModifier().attr("id", id).toAttrs()) { Text(value) }
}

@Composable
internal fun EditorAssistMetaText(value: String) {
    Span(EditorAssistMetaStyle.toModifier().toAttrs()) { Text(value) }
}

@Composable
internal fun EditorProposalTrigger(
    state: TranslationEditorState,
    row: KeyRow,
    onOpenUtility: (EditorUtilitySurface, EditorFocusRef) -> Unit,
) {
    if (state.proposalsByKey[row.key.id] == null) return
    Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
        Span(EditorMetaStyle.toModifier().toAttrs()) {
            Text(t("editor.editor_assist_content.agent_suggestion_available", "Agent proposal available"))
        }
        LButton(
            label = t("editor.editor_assist_content.open_assist", "Open Assist"),
            onClick = {
                onOpenUtility(
                    EditorUtilitySurface.ASSIST,
                    EditorFocusRef.ElementId(EDITOR_INLINE_ASSIST_TRIGGER_ID),
                )
            },
            modifier = Modifier.attr("id", EDITOR_INLINE_ASSIST_TRIGGER_ID),
            variant = LButtonVariant.GHOST,
            size = LButtonSize.SM,
        )
    }
}
